import { useCallback, useEffect, useMemo, useState } from "react";

import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import EditCalendarOutlined from "@mui/icons-material/EditCalendarOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import KeyboardArrowLeftRounded from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Skeleton,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import FilePreviewSideModal from "../../components/modals/FilePreviewSideModal";
import ReadinessDetail, {
  CollapseAllContext,
  SECTION_ID,
} from "../../components/closingReadiness/ReadinessDetail";
import LoanFactsStrip, {
  LoanFactsGear,
  useLoanFactKeys,
} from "../../components/closingReadiness/LoanFactsStrip";
import ScheduleEditForm from "../../components/closingReadiness/ScheduleEditForm";
import {
  CollapseAllButton,
  useCollapseAllState,
} from "../../components/closingReadiness/ReadinessBits";
// The panel body — status strip, callouts, readiness, the quiet sections —
// is shared with the drilldown Closing tab (ClosingPanelSections.js). The
// drawer only adds the identity crumb, "Open loan file" and the pager.
import {
  Callout,
  ClosingLogisticsSection,
  FundedLine,
  IconAction,
  PanelDivider,
  PeopleContactsSection,
  SectionHead,
  StatusStrip,
  WireInstructionsSection,
} from "../../components/closingReadiness/ClosingPanelSections";
import {
  eventNoun,
  isTrade,
  readinessToCard,
  scheduleFieldHref,
} from "../../components/closingReadiness/closingReadinessUtils";
import { closingReadinessApi } from "../../api/closingReadinessApi";
import { losPipelineApi } from "../../api/losPipelineApi";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { slate } from "../../theme/colors";
import {
  PANEL_DEFAULT_WIDTH,
  PANEL_ICON_BUTTON_SX,
  panelPaperSx,
  resizeHandleSx,
  resizeThumbSx,
} from "../../components/sidebar/shared/panelChrome";
import { partnerStorage } from "../../utils/storage/partnerStorage";
import { TYPE } from "../../components/closingReadiness/typography";
import {
  C,
  loanClosingDate,
  loanDisplayName,
  loanId,
  loanPropertyAddress,
} from "./closingCalendarUtils";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";

// The panel's edge padding (the mockup's 26px) — every full-bleed divider
// pulls back out by the same amount.
const PAD = "26px";

// The crumb-row controls: pager arrows, the facts gear and the close X — the
// same 30px header button the drilldown sidebars use (panelChrome.js).
const CRUMB_BUTTON_SX = PANEL_ICON_BUTTON_SX;

// ── Drawer width: wider by default, drag-resizable from its left edge ───────
// Remembered per partner build (see partnerStorage) so the closer's preferred
// width survives reloads. Clamped to the viewport on read.
const WIDTH_KEY = "closingDetailDrawer.width";
// Same opening width the drilldown panes use (panelChrome).
const DEFAULT_WIDTH = PANEL_DEFAULT_WIDTH;
const MIN_WIDTH = 440;
const EDGE_MARGIN = 16;
const maxWidth = () => Math.max(MIN_WIDTH, window.innerWidth - EDGE_MARGIN);
const readStoredWidth = () => {
  try {
    const v = Number(partnerStorage.getItem(WIDTH_KEY));
    return v >= MIN_WIDTH
      ? Math.min(v, maxWidth())
      : Math.min(DEFAULT_WIDTH, maxWidth());
  } catch (e) {
    return DEFAULT_WIDTH;
  }
};

const useResizableWidth = () => {
  const [width, setWidth] = useState(readStoredWidth);
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    if (!resizing) return undefined;
    // Right-docked: the panel grows as the cursor moves left.
    const onMove = (e) =>
      setWidth(
        Math.min(
          maxWidth(),
          Math.max(MIN_WIDTH, window.innerWidth - e.clientX),
        ),
      );
    const onUp = () => {
      setResizing(false);
      setWidth((w) => {
        try {
          partnerStorage.setItem(WIDTH_KEY, String(w));
        } catch (e) {
          /* storage unavailable */
        }
        return w;
      });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [resizing]);

  const startResize = (e) => {
    e.preventDefault();
    setResizing(true);
  };
  return { width, resizing, startResize };
};

// The grab strip on the drawer's left edge (shared look — panelChrome.js).
const ResizeHandle = ({ onMouseDown, active }) => (
  <Box
    onMouseDown={onMouseDown}
    sx={{
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      zIndex: 3,
      ...resizeHandleSx(active),
    }}
  >
    <Box className="resize-thumb" sx={resizeThumbSx(active)} />
  </Box>
);


// ── Drawer ───────────────────────────────────────────────────────────────────
// `loan` is a schedule CARD from GET /closing/schedule (closing-readiness.md
// §2). The drawer pulls the full readiness object for the loan (§3) and, on
// preclose, the loan itself for contacts + wire instructions. Date edits go
// through PUT /closing/schedule (§4) and hand the fresh readiness back;
// `onChanged` lets the calendar refetch its feed. `cards` (the calendar's
// visible cards in reading order) + `onSelect` drive the ‹ n / N › pager.
//
// `disableOpenLoan` is for opening this same panel FROM the loan (the
// drilldown header's "Closing sidebar"): everything is identical, but the
// primary button can't take you where you already are.
const ClosingDetailDrawer = ({
  loan: card,
  cards,
  onSelect,
  open,
  onClose,
  onChanged,
  disableOpenLoan = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isCloser, isCapitalMarkets } = usePermissionsHook();
  const [readiness, setReadiness] = useState(null);
  const [full, setFull] = useState(null);
  // The stored AI readiness review — fetched the same way the drilldown Closing
  // tab does, so the AI section is identical in both surfaces.
  const [aiFull, setAiFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { width, resizing, startResize } = useResizableWidth();
  // Which loan facts the strip shows — picked from the crumb-row gear,
  // remembered per partner build.
  const factKeys = useLoanFactKeys();
  // Expand / collapse all of the sub-sections INSIDE Readiness (scoped there;
  // the drawer's own sections fold one at a time). They start folded.
  const [collapseAll, setCollapseAll, focusSection] = useCollapseAllState({ initialOpen: false });
  const dealId = loanId(card);
  const trade = isTrade(card);
  const pipeline = card?.pipeline || (trade ? "acquisition" : "preclose");
  const loanUrl = `${trade ? "/acquisition" : "/loan"}/${dealId}/closing`;

  useEffect(() => {
    if (!open || !dealId) return undefined;
    let active = true;
    setReadiness(null);
    setFull(null);
    setAiFull(null);
    setEditing(false);
    setLoading(true);

    closingReadinessApi
      .getReadiness(dealId, { pipeline })
      .then((r) => {
        if (!active) return;
        setReadiness(r);
        // Only pull the full stored review when one exists (same gate as the
        // drilldown), else the AI section stays on its summary.
        if (r?.aiReview) {
          closingReadinessApi
            .getAiReview(dealId, { pipeline })
            .then((ai) => active && setAiFull(ai))
            .catch(() => active && setAiFull(null));
        }
      })
      .catch((err) =>
        console.error("ClosingDetailDrawer readiness error:", err),
      )
      .finally(() => active && setLoading(false));

    // Contacts, wire instructions and the facts strip's loan-only values
    // (purpose, base amount, rate, …) live on the loan, not the readiness
    // object — read it from the pipeline's own route.
    (trade ? losPipelineApi.getAcquisitionLoan(dealId) : losPipelineApi.getLoan(dealId))
      .then((data) => active && setFull(data))
      .catch((err) => console.error("ClosingDetailDrawer getLoan error:", err));

    return () => {
      active = false;
    };
  }, [open, dealId, pipeline, trade, refreshKey]);

  // After a missing document slot is filled: re-pull readiness + the loan (the
  // new file has to resolve for preview) and let the calendar refetch its feed.
  const onDocsChanged = useCallback(() => {
    setRefreshKey((k) => k + 1);
    if (typeof onChanged === "function") onChanged();
  }, [onChanged]);

  // The card is the header until readiness lands; readiness then wins — with
  // its nested scheduling / activity signals lifted to card shape so the date,
  // chips and counts read the same either way.
  const view = useMemo(
    () => (readiness ? { ...card, ...readinessToCard(readiness) } : card),
    [readiness, card],
  );
  const date = useMemo(() => loanClosingDate(view), [view]);
  // The feed labels a property "subject property" when it can't read the LOS
  // address shape; fall back to the loan's own subjectProperties when we have it.
  const property =
    loanPropertyAddress(view) ||
    (Array.isArray(full?.subjectProperties)
      ? addressLabel(full.subjectProperties.find((p) => addressLabel(p?.address))?.address)
      : null) ||
    null;

  const docsById = useMemo(() => {
    const m = new Map();
    (full?.loanDocuments || []).forEach((d) => {
      if (d?._id) m.set(String(d._id), d);
    });
    return m;
  }, [full?.loanDocuments]);
  const resolveDoc = useCallback(
    (id) => (id ? docsById.get(String(id)) || null : null),
    [docsById],
  );

  // Deep links: open ONE readiness sub-section and scroll it into view ("Go to
  // scheduling entries" in the callout, "detail in Documents ↑" in logistics).
  const jumpTo = useCallback(
    (id) => {
      focusSection(id);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    },
    [focusSection],
  );
  const jumpToDocuments = useCallback(() => jumpTo(SECTION_ID.documents), [jumpTo]);
  const jumpToScheduling = useCallback(() => jumpTo(SECTION_ID.scheduling), [jumpTo]);

  const canEdit = isAdmin || isCloser || isCapitalMarkets;
  const closed = !!view?.closed;

  // ‹ n / N › — where this card sits in the calendar's list, and the moves.
  const pager = useMemo(() => {
    const list = Array.isArray(cards) ? cards : [];
    if (list.length < 2 || typeof onSelect !== "function") return null;
    const index = list.findIndex((c) => loanId(c) === dealId);
    if (index < 0) return null;
    return {
      index,
      count: list.length,
      prev: index > 0 ? () => onSelect(list[index - 1]) : null,
      next: index < list.length - 1 ? () => onSelect(list[index + 1]) : null,
    };
  }, [cards, onSelect, dealId]);

  const onScheduleSaved = (res) => {
    setEditing(false);
    if (res?.readiness) setReadiness(res.readiness);
    if (typeof onChanged === "function") onChanged(res);
  };

  // Scheduling entries rows: edit one in place, or close the panel and jump
  // to where the loan file edits it (same gate as "Adjust dates").
  const scheduleEdit = {
    canEdit: canEdit && !closed,
    onSaved: onScheduleSaved,
    onGoTo: (field) => {
      const href = scheduleFieldHref({ dealId, readiness, pathname: location.pathname, field });
      if (!href) return;
      navigate(href);
      if (typeof onClose === "function") onClose();
    },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        // No dimming scrim: the calendar stays readable beside the panel; a
        // click on it still closes the drawer.
        backdrop: { sx: { background: "transparent" } },
        paper: {
          sx: {
            width: { xs: "100%", sm: `min(${width}px, 100%)` },
            ...panelPaperSx("right"),
            transition: resizing ? "none" : undefined,
          },
        },
      }}
    >
      {/* Drag shield: keeps mousemove flowing while the cursor crosses the
          page (and any iframe) during a resize. */}
      {resizing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 20001,
            cursor: "ew-resize",
          }}
        />
      )}
      <ResizeHandle onMouseDown={startResize} active={resizing} />

      <Box sx={{ flexGrow: 1, overflowY: "auto", px: PAD, pt: 2.75, pb: 3.75 }}>
        {/* Crumb: ids on the left; ‹ n / N › paging through the calendar's
            cards and the close X on the right. The status lives in the strip
            below. */}
        <Flx ac g={1.25} sx={{ mb: 1.25 }}>
          {view?.loanNumber != null && (
            <Txt sx={{ ...TYPE.monoId, fontWeight: 500 }}>#{view.loanNumber}</Txt>
          )}
          {view?.loanNumber != null && view?.syncLoanId && (
            <Txt sx={{ ...TYPE.monoId, color: slate[300] }}>·</Txt>
          )}
          {view?.syncLoanId && <Txt sx={TYPE.monoId}>{view.syncLoanId}</Txt>}
          <Flx ac g={0.5} sx={{ ml: "auto" }}>
            {pager && (
              <Flx ac g={0.25} sx={{ mr: 1 }}>
                <IconButton
                  size="small"
                  onClick={pager.prev}
                  disabled={!pager.prev}
                  aria-label="Previous loan"
                  sx={CRUMB_BUTTON_SX}
                >
                  <KeyboardArrowLeftRounded sx={{ fontSize: 18 }} />
                </IconButton>
                <Txt sx={{ ...TYPE.monoId, minWidth: 40, textAlign: "center" }}>
                  {pager.index + 1} / {pager.count}
                </Txt>
                <IconButton
                  size="small"
                  onClick={pager.next}
                  disabled={!pager.next}
                  aria-label="Next loan"
                  sx={CRUMB_BUTTON_SX}
                >
                  <KeyboardArrowRightRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Flx>
            )}
            <LoanFactsGear
              keys={factKeys.keys}
              onToggle={factKeys.toggle}
              onReset={factKeys.reset}
              trade={trade}
              sx={CRUMB_BUTTON_SX}
            />
            <IconButton size="small" onClick={onClose} aria-label="Close panel" sx={CRUMB_BUTTON_SX}>
              <CloseRounded sx={{ fontSize: 17 }} />
            </IconButton>
          </Flx>
        </Flx>

        <Txt
          sx={{
            ...TYPE.title,
            fontSize: 18,
            letterSpacing: "-0.02em",
            display: "block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {loanDisplayName(view)}
        </Txt>
        {/* The address line always takes its row so the strip below doesn't
            jump: the card often lacks it and it only resolves once the loan
            itself loads (preclose) — a skeleton holds the place until then. */}
        <Flx ac g={0.75} sx={{ mt: 0.5, minWidth: 0, height: 16 }}>
          <PlaceOutlined sx={{ fontSize: 13, color: C.faint, flexShrink: 0 }} />
          {property ? (
            <Txt sx={{ ...TYPE.sub, fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {property}
            </Txt>
          ) : !trade && !full ? (
            <Skeleton variant="text" width={220} sx={{ fontSize: 11, transform: "none", height: 11 }} />
          ) : (
            <Txt sx={{ ...TYPE.sub, fontSize: 11, color: C.faint }}>No property address on file</Txt>
          )}
        </Flx>

        {/* The loan's headline facts — status · product · purpose · amount by
            default, the gear picks more; remembered per partner build. */}
        <LoanFactsStrip view={view} full={full} keys={factKeys.keys} sx={{ mt: 2.25, mb: 0.5 }} />

        {/* The identity header ends here; the status strip starts a new zone. */}
        <Box sx={{ height: "1px", background: C.line, mx: `-${PAD}`, mt: 2.25 }} />

        <StatusStrip
          view={view}
          trade={trade}
          date={date}
          closed={closed}
          // Opened from the loan the card is a seed, so the level only arrives
          // with the readiness call — don't print a fallback verdict until then.
          pending={!readiness && view?.readinessLevel == null}
        />

        {/* Primary: open here. Secondary, as icons: a new tab (the calendar
            stays put) and the date editor. Same pipeline-correct URL for both. */}
        <Flx ac g={1.125} sx={{ mt: 1.75, minWidth: 0 }}>
          <Button
            variant="contained"
            disabled={disableOpenLoan}
            endIcon={<ArrowForwardRounded sx={{ fontSize: "16px !important" }} />}
            onClick={() => navigate(loanUrl)}
            sx={{
              flex: 1,
              minWidth: 0,
              height: 42,
              fontSize: 13.5,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              whiteSpace: "nowrap",
            }}
          >
            Open loan file
          </Button>
          <IconAction
            title="Open in new tab"
            label="Open in new tab"
            icon={OpenInNewRounded}
            onClick={() => window.open(loanUrl, "_blank", "noopener")}
          />
          {canEdit && !closed && (
            <IconAction
              title={editing ? "Cancel date edit" : "Adjust dates"}
              label={editing ? "Cancel" : "Adjust dates"}
              icon={editing ? CloseRounded : EditCalendarOutlined}
              onClick={() => setEditing((e) => !e)}
              disabled={!readiness}
            />
          )}
        </Flx>

        {editing && readiness && (
          <Box sx={{ mt: 1.75, border: `1px solid ${C.line}`, borderRadius: "10px", px: 2.5, py: 2.25 }}>
            <Txt sx={{ ...TYPE.sectionH, fontSize: 13, display: "block", mb: 2.5 }}>
              Adjust {eventNoun(readiness).toLowerCase()} dates
            </Txt>
            <ScheduleEditForm
              readiness={readiness}
              onSaved={onScheduleSaved}
              onCancel={() => setEditing(false)}
            />
          </Box>
        )}

        {/* One callout: what stands between this loan and a schedule, or the
            funded confirmation once it has closed. */}
        {closed ? (
          <FundedLine trade={trade} date={date} />
        ) : (
          <Callout view={view} readiness={readiness} onJumpToScheduling={jumpToScheduling} />
        )}

        <PanelDivider bleed={PAD} />

        {/* Readiness — the sidebar's reason for existing. */}
        <SectionHead
          icon={FactCheckOutlined}
          title="Readiness"
          right={
            readiness ? (
              <CollapseAllButton allOpen={collapseAll.open} onSet={setCollapseAll} />
            ) : null
          }
        />
        <Box sx={{ mt: "9px" }}>
          {loading && !readiness ? (
            <Flx ac g={1} sx={{ py: 1 }}>
              <CircularProgress size={14} />
              <Txt sx={TYPE.meta}>Loading readiness…</Txt>
            </Flx>
          ) : !readiness ? (
            <Txt sx={TYPE.meta}>
              Readiness unavailable.
              {Array.isArray(card?.reasons) && card.reasons.length ? ` ${card.reasons.join(" ")}` : ""}
            </Txt>
          ) : (
            <CollapseAllContext.Provider value={collapseAll}>
              <ReadinessDetail
                readiness={readiness}
                compact
                resolveDoc={resolveDoc}
                onPreviewDoc={setPreviewFile}
                aiReviewFull={aiFull}
                loan={full}
                onDocsChanged={onDocsChanged}
                hideHeader
                scheduleEdit={scheduleEdit}
              />
            </CollapseAllContext.Provider>
          )}
        </Box>

        <PanelDivider bleed={PAD} />

        {/* The quiet sections: flat rows that fold open one at a time. */}
        <Box>
          <ClosingLogisticsSection view={view} trade={trade} onJumpToDocuments={jumpToDocuments} />
          {/* People & contacts + wire — preclose only (needs the loan itself). */}
          {!trade && (
            <>
              <PeopleContactsSection full={full} view={view} />
              <WireInstructionsSection full={full} view={view} />
            </>
          )}
        </Box>
      </Box>

      <FilePreviewSideModal
        file={previewFile}
        loan={full}
        onClose={() => setPreviewFile(null)}
      />
    </Drawer>
  );
};

export default ClosingDetailDrawer;
